import { useEffect, useState } from "react";
import { X, Loader2, Send, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Email Proforma Invoice to the customer.
 *
 * The dispatcher already sees the exact figures in the Invoice Summary
 * card. Rather than re-derive on the backend and risk drifting from
 * what's on screen, the parent passes the computed `lineItems` and
 * `grandTotal` straight into this dialog — we ship those to the server,
 * which renders them onto a PDF and fires Resend.
 *
 * Follows the same async task-poll pattern as EmailInsurerDialog so a
 * slow PDF build never hits Cloudflare's edge timeout.
 */
export default function EmailProformaDialog({
  open,
  onOpenChange,
  job,
  lineItems,       // [{label, amount}] — same items the Print view uses
  grandTotal,      // pre-computed subtotal (ex-GST) sum, for the on-screen preview
  taxRate,         // e.g. 0.15
  taxLabel,        // e.g. "GST"
  taxInclusive = false,  // impounds: statutory fees already include GST
  defaultTo = "",        // impounds: prefilled from the registered owner
  defaultNote = "",      // impounds: release-due date + daily rate after it
  description,
}) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [taskState, setTaskState] = useState(null); // {status, progress, error, ...}

  useEffect(() => {
    if (!open) return;
    // Reset per-open so a previous send doesn't leak.
    setTo(defaultTo || "");
    setCc("");
    setNote(defaultNote || "");
    setTaskState(null);
    setSending(false);
  }, [open, job?.id, defaultTo, defaultNote]);

  // Impound amounts arrive GST-inclusive, so the tax comes OUT of the total
  // rather than going on top — otherwise a statutory capped fee would be
  // invoiced 15% high.
  const gross = Number(grandTotal || 0);
  const rate = Number(taxRate || 0.15);
  const total = taxInclusive ? +gross.toFixed(2) : +(gross * (1 + rate)).toFixed(2);
  const subtotal = taxInclusive ? +(total / (1 + rate)).toFixed(2) : +gross.toFixed(2);
  const gst = +(total - subtotal).toFixed(2);
  // Thousands separators, matching the impound list and receipts — a
  // five-figure storage bill is hard to read otherwise.
  const money = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

  const canSend =
    !sending &&
    validEmail(to) &&
    (lineItems || []).some((li) => Number(li.amount) > 0) &&
    (!cc.trim() ||
      cc.split(",").every((c) => !c.trim() || validEmail(c)));

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setTaskState({ status: "queued", progress: "queueing…" });
    try {
      const ccList = cc
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await api.post(
        `/jobs/${job.id}/email-proforma/send`,
        {
          to: to.trim(),
          cc: ccList.length ? ccList : null,
          line_items: (lineItems || [])
            .filter((li) => Number(li.amount) !== 0)
            .map((li) => ({ label: li.label, amount: Number(li.amount) })),
          note: note.trim() || null,
          tax_inclusive: taxInclusive,
        },
      );
      // Poll every 1.5s until sent / failed. Insurer dialog uses the same
      // cadence — plenty fast for a single-PDF build (usually ~1-3s).
      const taskId = data.task_id;
      const poll = async () => {
        try {
          const { data: t } = await api.get(
            `/jobs/${job.id}/email-proforma/task/${taskId}`,
          );
          setTaskState(t);
          if (t.status === "sent") {
            toast.success(`Proforma sent to ${to.trim()}`);
            setSending(false);
            return;
          }
          if (t.status === "failed") {
            toast.error(t.error || "Send failed");
            setSending(false);
            return;
          }
          setTimeout(poll, 1500);
        } catch (e) {
          setTaskState({ status: "failed", error: e?.message || "Poll failed" });
          setSending(false);
        }
      };
      setTimeout(poll, 800);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Send failed";
      toast.error(msg);
      setTaskState({ status: "failed", error: msg });
      setSending(false);
    }
  };

  const sent = taskState?.status === "sent";
  const failed = taskState?.status === "failed";
  const running = sending || (taskState && !sent && !failed);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="email-proforma-sheet"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
            Email Proforma Invoice
          </SheetTitle>
          <SheetDescription>
            {description ||
              "Send the customer a proforma invoice PDF with your bank details so they can pay by online banking."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4">
          {/* Recipient */}
          <div>
            <Label htmlFor="proforma-to" className="uppercase-label">
              Customer email
            </Label>
            <Input
              id="proforma-to"
              type="email"
              autoFocus
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
              disabled={running || sent}
              data-testid="proforma-to-input"
            />
          </div>

          <div>
            <Label htmlFor="proforma-cc" className="uppercase-label">
              CC (optional, comma-separated)
            </Label>
            <Input
              id="proforma-cc"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="office@yourtowco.co.nz"
              disabled={running || sent}
              data-testid="proforma-cc-input"
            />
          </div>

          <div>
            <Label htmlFor="proforma-note" className="uppercase-label">
              Short note (optional)
            </Label>
            <textarea
              id="proforma-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything extra to say. Leave blank for a standard send."
              rows={3}
              disabled={running || sent}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 text-sm resize-y"
              data-testid="proforma-note-input"
            />
          </div>

          {/* Preview */}
          <div className="rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3 space-y-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-900 dark:text-emerald-200">
              PDF preview · line items
            </div>
            {(lineItems || []).filter((li) => Number(li.amount) !== 0).length === 0 ? (
              <p className="text-xs text-rose-700 dark:text-rose-300 italic">
                Nothing to invoice — the summary is empty. Set a tow / storage /
                tow-out charge first.
              </p>
            ) : (
              <>
                {lineItems
                  .filter((li) => Number(li.amount) !== 0)
                  .map((li, i) => (
                    <div
                      key={i}
                      className="flex justify-between text-xs text-slate-700 dark:text-slate-200 gap-2"
                    >
                      <span className="truncate">{li.label}</span>
                      <span className="font-semibold tabular-nums">
                        {money(li.amount)}
                      </span>
                    </div>
                  ))}
                <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400 pt-1 border-t border-emerald-200 dark:border-emerald-800">
                  <span>Subtotal (ex {taxLabel || "GST"})</span>
                  <span className="tabular-nums">{money(subtotal)}</span>
                </div>
                <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400">
                  <span>
                    {taxLabel || "GST"} {(+(Number(taxRate || 0.15) * 100)).toFixed(2)}%
                  </span>
                  <span className="tabular-nums">{money(gst)}</span>
                </div>
                <div className="flex justify-between font-display text-base font-black text-emerald-900 dark:text-emerald-100 pt-1 border-t border-emerald-300 dark:border-emerald-700">
                  <span>Total due (incl {taxLabel || "GST"})</span>
                  <span className="tabular-nums" data-testid="proforma-preview-total">
                    {money(total)}
                  </span>
                </div>
              </>
            )}
            {taxInclusive && (
              <p className="text-[10px] text-slate-600 dark:text-slate-300 italic pt-1">
                Impound fees are set {taxLabel || "GST"}-inclusive by statute,
                so the tax shown is backed out of the total, not added to it.
              </p>
            )}
            <p className="text-[10px] text-slate-500 dark:text-slate-400 italic pt-1">
              Bank account &amp; payment reference are added to the PDF from
              Settings → Company Branding.
            </p>
          </div>

          {/* Live task progress */}
          {taskState && (
            <div
              className={`rounded-md border p-3 text-xs flex items-start gap-2 ${
                sent
                  ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-100"
                  : failed
                    ? "border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-100"
                    : "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200"
              }`}
              data-testid="proforma-task-state"
            >
              {sent ? (
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              ) : failed ? (
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-4 w-4 flex-shrink-0 mt-0.5 animate-spin" />
              )}
              <div className="flex-1">
                <div className="font-semibold">
                  {sent
                    ? "Sent"
                    : failed
                      ? "Send failed"
                      : "Working…"}
                </div>
                <div>
                  {failed
                    ? taskState.error || "Unknown error"
                    : taskState.progress || taskState.status}
                </div>
                {sent && taskState.reference && (
                  <div className="text-[11px] mt-1">
                    Reference <b>{taskState.reference}</b> · Total{" "}
                    <b>{money(taskState.grand_total)}</b>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={running}
              data-testid="proforma-cancel-btn"
            >
              <X className="h-4 w-4" />
              {sent ? "Close" : "Cancel"}
            </Button>
            {!sent && (
              <Button
                type="button"
                onClick={send}
                disabled={!canSend}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="proforma-send-btn"
              >
                {running ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {running ? "Sending…" : "Send Proforma"}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
